import { forwardRef, type KeyboardEvent } from 'react';
import styled, { css } from 'styled-components';
import { focusVisibleMixin } from '@elfsight/widget-ui';
import type { Section } from '../../../../../../types';

interface SectionsControlItemProps {
  data: Section;
  className?: string;
  selected?: boolean;
  onClick: () => void;
  onKeyDown: (e: KeyboardEvent) => void;
}

export const SectionsControlItem = forwardRef<
  HTMLDivElement,
  SectionsControlItemProps
>(({ data: { id, name }, className, selected, onClick, onKeyDown }, ref) => (
  <SectionsControlItemComponent
    ref={ref}
    id={id}
    role="tab"
    aria-selected={selected}
    tabIndex={selected ? 0 : -1}
    onClick={onClick}
    onKeyDown={onKeyDown}
    className={className}
    _selected={selected}
  >
    {name}
  </SectionsControlItemComponent>
));

export const SectionsControlItemComponent = styled.div<{
  _selected?: boolean;
}>`
  font-size: ${({
    theme: {
      sectionTabsFontSize: { fontSize }
    }
  }) => fontSize}px;
  line-height: 1.33;
  cursor: pointer;
  text-align: center;
  position: relative;
  padding-bottom: 2px;
  text-transform: ${({
    theme: {
      sectionTitleFontStyle: { textTransform }
    }
  }) => textTransform};

  ${({ _selected, theme: { colorScheme, textColor, accentColor } }) => css`
    ${!_selected &&
    css`
      ${colorScheme === 'light' &&
      css`
        color: #111;
      `}
      ${colorScheme === 'dark' &&
      css`
        color: #fff;
      `}
      ${colorScheme === 'custom' &&
      css`
        color: ${textColor};
      `}
    `}

    ${_selected &&
    accentColor &&
    css`
      color: ${accentColor};
    `}
  `};

  &::after {
    content: ' ';
    width: 100%;
    position: absolute;
    bottom: 0;
    height: 2px;
    left: 0;
    display: block;
    transition: 0.1s ease opacity;
    opacity: ${({ _selected }) => (_selected ? 1 : 0)};

    ${({ _selected, theme: { colorScheme, textColor, accentColor } }) => css`
      ${!_selected &&
      css`
        ${colorScheme === 'light' &&
        css`
          background: #111;
        `}
        ${colorScheme === 'dark' &&
        css`
          background: #fff;
        `}
        ${colorScheme === 'custom' &&
        css`
          background: ${textColor};
        `}
      `}

      ${_selected &&
      accentColor &&
      css`
        background: ${accentColor};
      `}
    `};
  }

  &:is(:hover, :focus-visible):after {
    opacity: 1;
  }

  ${focusVisibleMixin()}
`;
